/**
 * `/settings/devices` — paired Macs, iPhones and the browser extension.
 *
 * ## What changed in the 2026-08-29 sweep
 *
 * This screen was a placard: "Device pairing, revocation and last-seen. Blocked
 * on: Device auth is landing separately; the API's JWT and api-key paths return
 * 501 today and there is no devices.* procedure." Three of those four claims are
 * now false. `devices.list`, `devices.createPairingCode` and `devices.revoke`
 * are on the contract and wired in `apps/api` (the fourth,
 * `devices.updatePushToken`, is the phone's own call and has no UI here by
 * design — it carries no `deviceId` precisely so a client cannot register a
 * token against someone else's device).
 *
 * So this is now the real screen: the device list with its last check-in, a
 * revoke, and the pairing flow.
 *
 * ## The pairing code is shown ONCE and the UI must never pretend otherwise
 *
 * `devices.createPairingCode` returns the 8-character plaintext exactly once —
 * the server stores only its sha256 — and it expires in five minutes. There is
 * no procedure that could show it again. So the code is displayed immediately,
 * with a live countdown, and dismissing it says plainly that a new one has to be
 * minted. A "copy again later" affordance would be a button that cannot work.
 *
 * ## The "silent for over 24 hours" warning
 *
 * `lastSeenAt` is what the connection strip's stale-Mac warning is computed
 * from, so it is stated here in the same words rather than as a raw timestamp.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SkeletonRows,
  toast,
} from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { LaptopIcon, PuzzleIcon, SmartphoneIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type DevicePlatform,
  useCreatePairingCodeMutation,
  useDevicesQuery,
  useRevokeDeviceMutation,
} from '../../../api/queries.ts';
import { ConfirmDialog } from '../../../components/confirm-dialog.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { toClientError } from '../../../lib/errors.ts';
import { formatDateTime, relativeTime } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/settings/devices')({
  component: DevicesSettings,
  errorComponent: RouteErrorPanel,
});

const PLATFORMS: readonly {
  readonly value: DevicePlatform;
  readonly label: string;
  readonly icon: typeof LaptopIcon;
  readonly how: string;
}[] = [
  {
    value: 'macos',
    label: 'Mac',
    icon: LaptopIcon,
    how: 'Open the Communiful menu-bar app, choose “Pair with your workspace”, and type the code.',
  },
  {
    value: 'ios',
    label: 'iPhone',
    icon: SmartphoneIcon,
    how: 'Open the Communiful app on your phone, go to Settings → Pair, and type the code.',
  },
  {
    value: 'chrome_extension',
    label: 'Browser extension',
    icon: PuzzleIcon,
    how: 'Open the extension’s side panel, choose “Connect”, and type the code.',
  },
];

const PLATFORM_LABELS: Readonly<Record<string, string>> = {
  macos: 'Mac',
  ios: 'iPhone',
  chrome_extension: 'Browser extension',
};

/** Design's own threshold: a Mac silent for a day is what the strip warns about. */
const SILENT_MS = 24 * 60 * 60 * 1000;

function PairingCodeCard({
  code,
  expiresAt,
  how,
  onDismiss,
}: {
  code: string;
  expiresAt: string;
  how: string;
  onDismiss: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());

  // A five-minute code with no visible clock is a code people type at 5:30.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const secondsLeft = Math.max(0, Math.round((Date.parse(expiresAt) - now) / 1000));
  const expired = secondsLeft === 0;

  return (
    <Card className="border-primary/50">
      <CardHeader>
        <CardTitle>{expired ? 'That code has expired' : 'Type this code into the app'}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {expired ? (
          <p className="text-muted-foreground">
            Codes last five minutes and can only be used once. Mint a new one below.
          </p>
        ) : (
          <>
            {/* The visible code is `aria-hidden` and the spelled-out version is
                screen-reader-only: "AB3F9K2Q" in a 0.3em-tracked monospace font
                is read as one nonsense word, and a pairing code is exactly the
                string a person needs letter by letter. `aria-label` on a `<p>`
                does not work — a paragraph has no role that supports it. */}
            <p
              aria-hidden
              className="select-all font-mono text-3xl tracking-[0.3em]"
              data-testid="pairing-code"
            >
              {code}
            </p>
            <span className="sr-only">Pairing code: {code.split('').join(' ')}</span>
            <p className="text-muted-foreground">{how}</p>
            <p className="text-xs text-muted-foreground">
              Expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}.
              It works once, and this is the only time it is shown — closing this panel means
              minting a new one.
            </p>
          </>
        )}
        <Button size="sm" variant="outline" className="w-fit" onClick={onDismiss}>
          Done
        </Button>
      </CardContent>
    </Card>
  );
}

function DevicesSettings() {
  const devices = useDevicesQuery();
  const mint = useCreatePairingCodeMutation();
  const revoke = useRevokeDeviceMutation();
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string; how: string } | null>(
    null,
  );
  const [revoking, setRevoking] = useState<{ id: string; label: string } | null>(null);

  const items = devices.data?.items ?? [];
  const active = items.filter((device) => device.status === 'active');
  const revoked = items.filter((device) => device.status !== 'active');

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      {pairing === null ? null : (
        <PairingCodeCard
          code={pairing.code}
          expiresAt={pairing.expiresAt}
          how={pairing.how}
          onDismiss={() => setPairing(null)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pair a device</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-muted-foreground">
            Each device gets its own credential, so revoking one never signs out the others.
          </p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((platform) => (
              <Button
                key={platform.value}
                size="sm"
                variant="outline"
                disabled={mint.isPending}
                onClick={() =>
                  mint.mutate(platform.value, {
                    onSuccess: (minted) =>
                      setPairing({
                        code: minted.code,
                        expiresAt: minted.expiresAt,
                        how: platform.how,
                      }),
                    onError: (error) =>
                      toast.error('Could not create a pairing code', {
                        description: toClientError(error).message,
                      }),
                  })
                }
              >
                <platform.icon aria-hidden />
                Pair a {platform.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your devices</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {devices.isLoading ? (
            <SkeletonRows rows={3} />
          ) : devices.isError ? (
            <EmptyState
              kind="error"
              title="Could not load your devices"
              description={toClientError(devices.error).message}
              action={
                <Button size="sm" variant="outline" onClick={() => void devices.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : active.length === 0 ? (
            <p className="text-muted-foreground">
              Nothing paired yet. Pair your Mac to record meetings, or your phone to add people you
              just met.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {active.map((device) => {
                const silent =
                  device.lastSeenAt === null ||
                  Date.now() - Date.parse(device.lastSeenAt) > SILENT_MS;
                return (
                  <li
                    key={device.id}
                    className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-3"
                  >
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="truncate font-medium">
                        {device.name ?? PLATFORM_LABELS[device.platform] ?? device.platform}
                      </span>
                      <Badge variant="muted" className="shrink-0">
                        {PLATFORM_LABELS[device.platform] ?? device.platform}
                      </Badge>
                      {silent ? (
                        <Badge variant="warning" className="shrink-0">
                          quiet
                        </Badge>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground sm:ml-auto">
                      {device.lastSeenAt === null
                        ? 'Never checked in'
                        : `Last seen ${relativeTime(device.lastSeenAt) ?? formatDateTime(device.lastSeenAt)}`}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="w-fit text-destructive"
                      onClick={() =>
                        setRevoking({
                          id: device.id,
                          label: device.name ?? PLATFORM_LABELS[device.platform] ?? device.platform,
                        })
                      }
                    >
                      Revoke
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          {revoked.length === 0 ? null : (
            <p className="mt-3 text-xs text-muted-foreground">
              {revoked.length} revoked device{revoked.length === 1 ? '' : 's'} kept for the record.
            </p>
          )}
        </CardContent>
      </Card>

      {revoking === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
          title={`Revoke ${revoking.label}?`}
          description="That device stops being able to reach your workspace immediately. Anything it has already sent stays. You can pair it again with a fresh code."
          confirmLabel="Revoke"
          onConfirm={() => revoke.mutateAsync(revoking.id)}
        />
      )}
    </div>
  );
}
